import { addNotification } from "store/notification/slice";
import { ConfirmStep } from "./Steps/ConfirmStep";
import { getToken } from "store/user/thunks";
import { ModalBase } from "../ModalBase";
import { NotificationType } from "store/notification/types";
import { RootState } from "store/reducers";
import {
    Dispatch,
    SetStateAction,
    useCallback,
    useEffect,
    useMemo,
    useState
} from "react";
import { useDispatch } from "react-redux";
import { useSelector } from "react-redux";
import { useSwitcherClient } from "hooks/useSwitcherClient";
import { useTranslation } from "react-i18next";
import { WarningStep } from "./Steps/WarningStep";
import { AccountAlreadyDeleted } from "./Steps/AccountAlreadyDeletedStep";
import { Wizard } from "components/wizards/Wizard";
import { useSwitcherSdk } from "hooks/useSwitcherSdk";
import {
    versionGreaterOrEqual,
    extractMajorAndMinorVersion
} from "helpers/strings";
import { IUserLogin } from "store/user/types";
import { AppDispatch } from "store/store";
import { useLogout } from "hooks/useLogout";

const appVersionWithLogout = import.meta.env.VITE_APP_VERSION_WITH_LOGOUT;

export enum AccountDeleteModalSteps {
    Warning = "warning",
    Confirm = "confirm",
    AccountDeleted = "account-deleted"
}

export interface AccountDeleteModalProps {
    isOpen: boolean;
    setIsOpen: Dispatch<SetStateAction<boolean>>;
}

export const AccountDeleteModal: React.FC<AccountDeleteModalProps> = ({
    isOpen,
    setIsOpen
}) => {
    const { t } = useTranslation();
    const [activeStep, setActiveStep] = useState<AccountDeleteModalSteps>(
        AccountDeleteModalSteps.Warning
    );
    const dispatch = useDispatch<AppDispatch>();
    const { isInBrowser } = useSwitcherSdk();
    const [error, setError] = useState<string | null>(null);
    const [password, setPassword] = useState<string>("");
    const [submitDisabled, setSubmitDisabled] = useState<boolean>(true);
    const [shouldNotCloseOnSuccess, setShouldNotCloseOnSuccess] =
        useState<boolean>(true);
    const user = useSelector((state: RootState) => state.user);
    const { switcherSdk } = useSwitcherSdk();
    const { handleLogout: logoutOnBrowser } = useLogout();
    const [major, minor] = useMemo(
        () => extractMajorAndMinorVersion(appVersionWithLogout),
        []
    );
    const appVersionHasLogout = useMemo(
        () =>
            !isInBrowser &&
            versionGreaterOrEqual(switcherSdk.appVersion, major, minor),
        [switcherSdk.appVersion, isInBrowser, major, minor]
    );

    useEffect(() => {
        if (activeStep === AccountDeleteModalSteps.Confirm) {
            setSubmitDisabled(!password ? true : false);
        }
        if (
            activeStep === AccountDeleteModalSteps.Warning ||
            activeStep === AccountDeleteModalSteps.AccountDeleted
        ) {
            setSubmitDisabled(false);
        }
    }, [activeStep, password]);

    const modalHeader = useMemo(() => {
        if (isInBrowser) {
            switch (activeStep) {
                case AccountDeleteModalSteps.Warning:
                    return t("account-delete:account-deletion");
                case AccountDeleteModalSteps.Confirm:
                    return t("account-delete:confirm-deletion");
                case AccountDeleteModalSteps.AccountDeleted:
                    return t("account-delete:account-deletion");
                default:
                    return "";
            }
        } else if (!isInBrowser) {
            return null;
        }
    }, [activeStep, t, isInBrowser]);

    const { data: hasDeletionRequest } = useSwitcherClient(
        (client) => client.deletionRequests_GetDeleteionRequestById,
        {
            suppressErrorBoundary: true,
            requestImmediately: true,
            args: [user.userInfo?.User?.Details?.Id]
        }
    );

    const { dispatchApiRequest: requestAccountDeletion } = useSwitcherClient(
        (client) => client.deletionRequests_PostDeletionRequest,
        {
            requestImmediately: false,
            args: [user.userInfo?.User?.Details?.Id],
            onSuccess: () => {
                if (isInBrowser) {
                    logoutOnBrowser("account-delete:successful-delete");
                    return;
                }

                // check if app has logout functionality for after deletion
                if (appVersionHasLogout) {
                    // if app version is 13.8 or greater, logout
                    switcherSdk.logout();
                }
            },
            onError: () => {
                dispatch(
                    addNotification({
                        type: NotificationType.Danger,
                        message: "delete-account:deletion-request-failure"
                    })
                );
            }
        }
    );

    const accountSetForDeletion = useMemo(
        () =>
            user.userInfo?.Claims?.Details?.FeatureClaims.includes("deleted") ||
            hasDeletionRequest,
        [user.userInfo?.Claims?.Details?.FeatureClaims, hasDeletionRequest]
    );

    useEffect(() => {
        if (accountSetForDeletion) {
            // in old versions of the app, we show a modal telling the user their account is deleted
            // the button should close the modal
            setActiveStep(AccountDeleteModalSteps.AccountDeleted);
            setShouldNotCloseOnSuccess(false);
        }
    }, [accountSetForDeletion, hasDeletionRequest]);

    const resetModal = useCallback(() => {
        setActiveStep(
            !accountSetForDeletion
                ? AccountDeleteModalSteps.Warning
                : AccountDeleteModalSteps.AccountDeleted
        );
        setPassword("");
        setError(null);
    }, [setActiveStep, setPassword, setError, accountSetForDeletion]);

    const reauthenticate = useCallback(async () => {
        if (accountSetForDeletion) return;
        try {
            await dispatch(
                getToken({
                    username: user.userInfo?.User?.Details?.Email,
                    password
                } as IUserLogin)
            );
            await requestAccountDeletion();
        } catch (e) {
            setPassword("");
            setSubmitDisabled(true);
            if (JSON.parse(e.message).error === "invalid_grant") {
                setError(t("account-delete:invalid-password"));
            }
        }
    }, [
        user.userInfo?.User?.Details?.Email,
        dispatch,
        password,
        requestAccountDeletion,
        t,
        accountSetForDeletion
    ]);

    const handleContinueClick = useCallback(async () => {
        if (activeStep === AccountDeleteModalSteps.Warning) {
            setActiveStep(AccountDeleteModalSteps.Confirm);
        } else if (activeStep === AccountDeleteModalSteps.Confirm) {
            try {
                await reauthenticate();
            } catch (e) {
                setPassword("");
                dispatch(
                    addNotification({
                        type: NotificationType.Danger,
                        message: "delete-account:deletion-request-failure"
                    })
                );
            }
        }
    }, [activeStep, dispatch, reauthenticate]);

    const successButton = useMemo(() => {
        // pass null so the button does not render in app's post-deletion webview
        if (accountSetForDeletion && !isInBrowser) {
            return null;
        }
        if (!accountSetForDeletion) {
            return t("buttons:delete");
        }
    }, [accountSetForDeletion, isInBrowser, t]);

    return (
        <ModalBase
            isOpen={isOpen}
            setIsOpen={setIsOpen}
            successButton={successButton}
            onSuccess={() => handleContinueClick()}
            successDisabled={submitDisabled}
            shouldNotCloseOnSuccess={shouldNotCloseOnSuccess}
            dismissButton={isInBrowser ? t("buttons:cancel") : null}
            onDismiss={() => resetModal()}
            header={modalHeader}
            variant="fullscreen"
        >
            <Wizard
                activeStep={activeStep}
                steps={[
                    {
                        id: AccountDeleteModalSteps.Warning,
                        component: <WarningStep />
                    },
                    {
                        id: AccountDeleteModalSteps.Confirm,
                        component: (
                            <ConfirmStep
                                password={password}
                                setPassword={setPassword}
                                error={error}
                            />
                        )
                    },
                    {
                        id: AccountDeleteModalSteps.AccountDeleted,
                        component: <AccountAlreadyDeleted />
                    }
                ]}
            />
        </ModalBase>
    );
};
